"use client";

import { useActionState, useCallback } from "react";
import { recordEmailSubmission } from "@marketing-sites/analytics/email-submission";
import { formDataToObject } from "@marketing-sites/utils/form-data-to-object";
import { firePostSubmissionEffects } from "./post-submission-effects";
import type { Return } from "./types";

type SubmitAction = (
  prevState: Return | undefined,
  formData: FormData,
) => Return | Promise<Return>;

type UseMarketoFormOptions = {
  /** The server action to wrap with useActionState */
  action: SubmitAction;
  /** The Marketo form ID (numeric string) for GTM and lead recovery */
  formId: string;
  /** The analytics location string (e.g. "Form", "Startup Pack Form Gate") */
  analyticsLocation: string;
  submitMetadata: { analyticsId: string };
  /** Called after successful submission, once all automatic behaviors have fired */
  onSuccess?: (status: Return) => void;
  /** Additional metadata merged into the GTM custom event */
  gtmEventMetadata?: Record<string, unknown>;
};

type UseMarketoFormReturn = {
  /** Current submission status (undefined before first submission) */
  status: Return | undefined;
  /** Form action to pass to <form action={formAction}> or <Form action={formAction}> */
  formAction: (payload: FormData) => void;
  /** Whether a submission is in flight */
  pending: boolean;
  /** onSubmit handler for <form> — records email submission for lead recovery */
  handleFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Record an email submission manually (for programmatic submissions like useGuardedFormSubmit) */
  recordSubmission: (values: Record<string, string>) => void;
};

export function useMarketoForm({
  action,
  formId,
  analyticsLocation,
  submitMetadata,
  onSuccess,
  gtmEventMetadata,
}: UseMarketoFormOptions): UseMarketoFormReturn {
  // Wrap the caller's server action to fire post-submission side effects
  // (analytics, GTM) automatically on success.
  const wrappedAction: SubmitAction = async (prevState, formData) => {
    const result = await action(prevState, formData);
    if (!result.success) return result;

    if (result.formValues) {
      firePostSubmissionEffects(
        {
          formId,
          analyticsLocation,
          analyticsId: submitMetadata.analyticsId,
          gtmEventMetadata,
        },
        result.formValues,
      );
    }

    onSuccess?.(result);

    return result;
  };

  const [status, formAction, pending] = useActionState(
    wrappedAction,
    undefined,
  );

  // Lead recovery — onSubmit handler that records the email submission
  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      const formData = new FormData(e.currentTarget);
      const values = formDataToObject(formData);
      recordEmailSubmission(formId, values).catch(console.error);
    },
    [formId],
  );

  // Escape hatch for programmatic submissions (e.g. useGuardedFormSubmit)
  const recordSubmission = useCallback(
    (values: Record<string, string>) => {
      recordEmailSubmission(formId, values).catch(console.error);
    },
    [formId],
  );

  return {
    status,
    formAction,
    pending,
    handleFormSubmit,
    recordSubmission,
  };
}
